import { useMemo } from "react";
import { Checkbox } from "../../../app/components/ui/checkbox";
import { cn } from "../../../app/components/ui/utils";
import type { Category } from "../hooks/useCategories";

type Node = Category & { children: Node[] };

function buildTree(list: Category[]): Node[] {
  const byId = new Map<string, Node>();
  for (const c of list) byId.set(c.id, { ...c, children: [] });

  const roots: Node[] = [];
  for (const n of byId.values()) {
    if (n.parent_id && byId.has(n.parent_id)) {
      byId.get(n.parent_id)!.children.push(n);
    } else {
      roots.push(n);
    }
  }

  const sortRec = (nodes: Node[]) => {
    nodes.sort((a, b) => a.name.localeCompare(b.name));
    for (const n of nodes) sortRec(n.children);
  };
  sortRec(roots);

  return roots;
}

export function CategoryTreeSelect({
  categories,
  value,
  onChange,
  className,
}: {
  categories: Category[];
  value: string[];
  onChange: (next: string[]) => void;
  className?: string;
}) {
  const tree = useMemo(() => buildTree(categories), [categories]);

  function toggle(id: string) {
    if (value.includes(id)) onChange(value.filter((x) => x !== id));
    else onChange([...value, id]);
  }

  function render(nodes: Node[], depth: number) {
    return (
      <div className={cn(depth === 0 ? "space-y-1" : "space-y-1")}>
        {nodes.map((n) => (
          <div key={n.id} className="space-y-1">
            <label
              className={cn(
                "flex items-center gap-2 rounded-md px-2 py-1 hover:bg-accent/50 cursor-pointer",
                depth > 0 && "ml-4",
              )}
              style={depth > 0 ? { marginLeft: depth * 12 } : undefined}
            >
              <Checkbox checked={value.includes(n.id)} onCheckedChange={() => toggle(n.id)} />
              <span className="text-sm">{n.name}</span>
            </label>
            {n.children.length ? render(n.children, depth + 1) : null}
          </div>
        ))}
      </div>
    );
  }

  return <div className={cn("rounded-md border p-2", className)}>{render(tree, 0)}</div>;
}

